import { clampDateTo, datePl, todayInWarsaw, type VehicleAssignmentDto, type VehicleDto } from '@bqcrm/shared';
import { Alert, Badge, Button, Group, Modal, Select, SegmentedControl, Stack, Table, Text, Textarea, TextInput } from '@mantine/core';
import { IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../../api';
import { QueryState } from '../../../components/QueryState';
import { EmptyState, SectionCard } from '../../../components/ui';
import { formatDateTime } from '../../../format';
import { notifyError, notifyOk } from '../../../notify';
import { usePeople, useTeams } from '../../planning/common';
import { useFleetInvalidate } from '../common';

function AssignModal({ v, current, onClose }: { v: VehicleDto; current: VehicleAssignmentDto | null; onClose: () => void }) {
  const people = usePeople();
  const teams = useTeams();
  const invalidate = useFleetInvalidate();
  const [holder, setHolder] = useState<'osoba' | 'ekipa'>('osoba');
  const [userId, setUserId] = useState<string | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [responsibleId, setResponsibleId] = useState<string | null>(null);
  const [validFrom, setValidFrom] = useState(todayInWarsaw());
  const [validTo, setValidTo] = useState('');
  const [comment, setComment] = useState('');
  const team = teams.data?.find((t) => t.id === teamId);
  const leaderName = team?.leaderId ? people.data?.find((p) => p.id === team.leaderId)?.fullName : null;
  const save = useMutation({
    mutationFn: () =>
      api<VehicleAssignmentDto[]>(`/fleet/vehicles/${v.id}/assignments`, {
        method: 'POST',
        body: { holder, userId: holder === 'osoba' ? userId : null, teamId: holder === 'ekipa' ? teamId : null, responsibleId: holder === 'ekipa' ? responsibleId : null, validFrom, validTo: validTo || null, comment },
      }),
    onSuccess: () => {
      notifyOk('Zapisano przypisanie');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  const peopleData = (people.data ?? []).map((p) => ({ value: p.id, label: p.fullName }));
  const valid = (holder === 'osoba' ? !!userId : !!teamId) && !!validFrom;
  return (
    <Modal opened onClose={onClose} title="Nowe przypisanie pojazdu" size="lg">
      <Stack gap="sm">
        {current && (
          <Alert color="blue" variant="light" py="xs">
            Obecne przypisanie ({current.holder === 'ekipa' ? `ekipa ${current.team?.name ?? ''}` : current.user?.name}) zostanie zamknięte dniem poprzedzającym datę „od”
            {current.validFrom === validFrom ? ' — zaczęło się tego dnia, więc zostanie w historii jako przekazane w dniu rozpoczęcia' : ''}.
          </Alert>
        )}
        {(people.error || teams.error) && (
          <Alert color="yellow" variant="light" py="xs">
            Nie udało się pobrać listy pracowników lub ekip (potrzebny dostęp do Kalendarza).
          </Alert>
        )}
        <SegmentedControl
          value={holder}
          onChange={(x) => setHolder(x as 'osoba' | 'ekipa')}
          data={[
            { value: 'osoba', label: 'Pracownik' },
            { value: 'ekipa', label: 'Ekipa' },
          ]}
        />
        {holder === 'osoba' ? (
          <Select label="Pracownik" required searchable data={peopleData} value={userId} onChange={setUserId} />
        ) : (
          <>
            <Select label="Ekipa" required searchable data={(teams.data ?? []).filter((t) => t.active).map((t) => ({ value: t.id, label: t.name }))} value={teamId} onChange={setTeamId} />
            <Select
              label="Odpowiedzialny"
              description={leaderName ? `Puste — lider ekipy (${leaderName}). Zmiana lidera nie zmieni odpowiedzialnego po cichu — pojawi się do potwierdzenia.` : 'Puste — lider ekipy.'}
              clearable
              searchable
              data={peopleData}
              value={responsibleId}
              onChange={setResponsibleId}
            />
          </>
        )}
        <Group grow>
          <TextInput label="Od" type="date" required value={validFrom} onChange={(e) => {
            const f = e.currentTarget.value;
            setValidFrom(f);
            setValidTo((t) => clampDateTo(f, t, { keepEmpty: true }));
          }} />
          <TextInput label="Do" description="puste — bez końca" type="date" min={validFrom || undefined} value={validTo} onChange={(e) => setValidTo(e.currentTarget.value)} />
        </Group>
        <Textarea label="Komentarz" autosize minRows={2} value={comment} onChange={(e) => setComment(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            Przypisz pojazd
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function EndModal({ a, onClose }: { a: VehicleAssignmentDto; onClose: () => void }) {
  const invalidate = useFleetInvalidate();
  const [validTo, setValidTo] = useState(todayInWarsaw());
  const [comment, setComment] = useState('');
  const save = useMutation({
    mutationFn: () => api(`/fleet/assignments/${a.id}/end`, { method: 'POST', body: { validTo, comment } }),
    onSuccess: () => {
      notifyOk('Zakończono przypisanie');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Zakończenie przypisania">
      <Stack gap="sm">
        <TextInput label="Ostatni dzień przypisania" type="date" min={a.validFrom} required value={validTo} onChange={(e) => setValidTo(e.currentTarget.value)} />
        <Textarea label="Komentarz" autosize minRows={2} value={comment} onChange={(e) => setComment(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button color="red" disabled={!validTo} loading={save.isPending} onClick={() => save.mutate()}>
            Zakończ przypisanie
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Zmiana lidera ekipy — zarządzający potwierdza nowego odpowiedzialnego albo zostawia obecnego. */
function LeaderBanner({ v, a }: { v: VehicleDto; a: VehicleAssignmentDto }) {
  const invalidate = useFleetInvalidate();
  const people = usePeople();
  const [decision, setDecision] = useState<'nowy_lider' | 'bez_zmian' | null>(null);
  const [validFrom, setValidFrom] = useState(todayInWarsaw());
  const [comment, setComment] = useState('');
  const leader = a.team?.leaderId ? (people.data?.find((p) => p.id === a.team!.leaderId)?.fullName ?? 'nowy lider') : 'brak lidera';
  const save = useMutation({
    mutationFn: () => api(`/fleet/vehicles/${v.id}/leader`, { method: 'POST', body: { decision, validFrom, comment } }),
    onSuccess: () => {
      notifyOk(decision === 'nowy_lider' ? 'Odpowiedzialnym jest nowy lider' : 'Odpowiedzialny bez zmian');
      invalidate();
      setDecision(null);
    },
    onError: notifyError,
  });
  return (
    <Alert color="orange" icon={<IconAlertTriangle size={18} />} title="Zmiana lidera ekipy — do potwierdzenia">
      <Stack gap="xs">
        <Text size="sm">
          Ekipa {a.team?.name} ma teraz lidera: <b>{leader}</b>. Odpowiedzialny z przypisania: <b>{a.responsible.name}</b>. Nic nie zmieniło się automatycznie.
        </Text>
        {v.can.manage && (
          <Group gap="xs">
            <Button size="xs" onClick={() => setDecision('nowy_lider')}>
              Odpowiedzialny: nowy lider
            </Button>
            <Button size="xs" variant="default" onClick={() => setDecision('bez_zmian')}>
              Zostaw obecnego
            </Button>
          </Group>
        )}
      </Stack>
      <Modal opened={!!decision} onClose={() => setDecision(null)} title={decision === 'nowy_lider' ? 'Nowy odpowiedzialny — lider ekipy' : 'Pozostawienie odpowiedzialnego'}>
        <Stack gap="sm">
          {decision === 'nowy_lider' && <TextInput label="Od" type="date" required value={validFrom} onChange={(e) => setValidFrom(e.currentTarget.value)} description="Nowy wpis w historii przypisań" />}
          <Textarea label="Komentarz" autosize minRows={2} value={comment} onChange={(e) => setComment(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDecision(null)}>
              Anuluj
            </Button>
            <Button loading={save.isPending} disabled={!validFrom} onClick={() => save.mutate()}>
              Potwierdź
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Alert>
  );
}

/** Przypisanie pojazdu do osoby albo ekipy z historią (bez nakładania się). */
export function AssignmentTab({ v }: { v: VehicleDto }) {
  const q = useQuery({ queryKey: ['fleet', 'vehicle', v.id, 'assignments'], queryFn: () => api<VehicleAssignmentDto[]>(`/fleet/vehicles/${v.id}/assignments`) });
  const [adding, setAdding] = useState(false);
  const [ending, setEnding] = useState<VehicleAssignmentDto | null>(null);
  const today = todayInWarsaw();
  const current = (q.data ?? []).find((a) => a.validFrom <= today && (!a.validTo || a.validTo >= today)) ?? null;
  const changed = (q.data ?? []).find((a) => a.leaderChanged && (!a.validTo || a.validTo >= today));
  return (
    <Stack gap="md">
      {changed && <LeaderBanner v={v} a={changed} />}
      <SectionCard
        title="Historia przypisań"
        padding={0}
        actions={
          v.can.manage &&
          v.status === 'aktywny' && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdding(true)}>
              Przypisz pojazd
            </Button>
          )
        }
      >
        <QueryState isLoading={q.isLoading} error={q.error}>
          {q.data?.length ? (
            <Table.ScrollContainer minWidth={760}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Komu</Table.Th>
                    <Table.Th>Odpowiedzialny</Table.Th>
                    <Table.Th>Okres</Table.Th>
                    <Table.Th>Komentarz</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {q.data.map((a) => (
                    <Table.Tr key={a.id}>
                      <Table.Td>
                        <Text size="sm" fw={a === current ? 600 : undefined}>
                          {a.holder === 'ekipa' ? `Ekipa ${a.team?.name ?? ''}` : (a.user?.name ?? '—')}
                        </Text>
                        {a === current && (
                          <Badge size="xs" variant="light" color="green">
                            bieżące
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{a.responsible.name}</Text>
                        {a.responsibleFromLeader && (
                          <Text size="xs" c="dimmed">
                            lider ekipy z chwili przypisania
                          </Text>
                        )}
                        {a.leaderChanged && (
                          <Badge size="xs" color="orange" variant="light">
                            zmiana lidera
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {a.validTo && a.validTo < a.validFrom ? (
                            `przekazany w dniu rozpoczęcia (${datePl(a.validFrom)})`
                          ) : (
                            <>
                              od {datePl(a.validFrom)}
                              {a.validTo ? ` do ${datePl(a.validTo)}` : ''}
                            </>
                          )}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{a.comment || '—'}</Text>
                        <Text size="xs" c="dimmed">
                          {a.createdByName ?? '—'} · {formatDateTime(a.createdAt)}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        {v.can.manage && (!a.validTo || a.validTo >= today) && (
                          <Button size="compact-sm" variant="subtle" color="red" onClick={() => setEnding(a)}>
                            Zakończ
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <EmptyState compact title="Pojazd nie był jeszcze przypisany" hint="Przypisz go do pracownika albo ekipy — odpowiedzialny dostaje zgłoszenia i podaje przebieg." />
          )}
        </QueryState>
      </SectionCard>
      {adding && <AssignModal v={v} current={current} onClose={() => setAdding(false)} />}
      {ending && <EndModal a={ending} onClose={() => setEnding(null)} />}
    </Stack>
  );
}
